/** The suggested fixes of one change's conflicts (`config.fixFor`) — an email contact
 *  another user already has offers "Make user #9050 the owner instead". A fix is ONE
 *  undoable draft step over all the paths it touches (drop the contact edit, queue the
 *  owner change), so ⌘Z takes it back whole. Rendered under the field (a list widget:
 *  under its list) and on its ledger row. */
import { Box, Button } from '@mui/material';

import type { RecordKey } from '../draft/changeSet';
import { useDraft } from '../draft/DraftProvider';
import { useRecordEditor } from '../editor/context';
import type { RecordFix } from '../editor/types';

export function useConflictFixes(rec: RecordKey, path: string): RecordFix[] {
  const { config, conflictsFor } = useRecordEditor();
  const { state } = useDraft();
  const { fixFor } = config;
  if (!fixFor) return [];
  const seen = new Set<string>();
  const out: RecordFix[] = [];
  for (const c of conflictsFor(rec, path)) {
    const fix = fixFor(rec, path, c, state);
    if (fix && !seen.has(fix.label)) {
      seen.add(fix.label);
      out.push(fix);
    }
  }
  return out;
}

export function ConflictFixes({ rec, path }: { rec: RecordKey; path: string }) {
  const fixes = useConflictFixes(rec, path);
  const { dispatch, baselineOf } = useDraft();
  if (fixes.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} data-conflict-fixes>
      {fixes.map((f) => (
        <Button
          key={f.label}
          size="small"
          variant="outlined"
          sx={{ py: 0, minHeight: 24 }}
          onClick={() =>
            dispatch({
              type: 'SET',
              label: f.label,
              changes: f.changes.map((c) => ({ rec: c.rec, path: c.path, from: baselineOf(c.rec, c.path), to: c.to })),
            })
          }
        >
          {f.label}
        </Button>
      ))}
    </Box>
  );
}
